import * as React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { meApi } from '@/api/me';
import { botsApi } from '@/api/bots';
import { setApiWorkspace } from '@/api/client';
import { useAuth } from '@/auth/AuthContext';
import type { Bot, OrgSummary } from '@/api/types';

const STORAGE_KEY = 'zorro-ai-workspace';

interface PersistedSelection {
  orgId: string;
  botId: string | null;
}

function readSelection(): PersistedSelection | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as PersistedSelection;
    if (!parsed.orgId) return null;
    return parsed;
  } catch {
    return null;
  }
}

function writeSelection(sel: PersistedSelection): void {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(sel));
}

function clearSelection(): void {
  localStorage.removeItem(STORAGE_KEY);
}

interface WorkspaceContextValue {
  orgs: OrgSummary[];
  bots: Bot[];
  currentOrgId: string | null;
  currentBotId: string | null;
  currentOrg: OrgSummary | null;
  currentBot: Bot | null;
  loading: boolean;
  noMemberships: boolean;
  setOrg: (orgId: string) => void;
  setBot: (botId: string | null) => void;
  refreshBots: () => void;
}

const WorkspaceContext = React.createContext<WorkspaceContextValue | undefined>(undefined);

export function WorkspaceProvider({ children }: { children: React.ReactNode }): JSX.Element {
  const { credentials, user } = useAuth();
  const queryClient = useQueryClient();
  const [currentOrgId, setCurrentOrgId] = React.useState<string | null>(
    () => readSelection()?.orgId ?? null,
  );
  const [currentBotId, setCurrentBotId] = React.useState<string | null>(
    () => readSelection()?.botId ?? null,
  );

  const enabled = !!credentials && !!user;

  const orgsQuery = useQuery({
    queryKey: ['me', 'orgs'],
    queryFn: () => meApi.orgs(),
    enabled,
    staleTime: 30_000,
  });

  const botsQuery = useQuery({
    queryKey: ['org', 'bots', currentOrgId],
    queryFn: () => botsApi.list(),
    enabled: enabled && !!currentOrgId,
    staleTime: 10_000,
  });

  const orgs = React.useMemo(() => orgsQuery.data?.items ?? [], [orgsQuery.data]);
  const bots = React.useMemo(() => botsQuery.data?.items ?? [], [botsQuery.data]);

  React.useEffect(() => {
    if (!credentials) {
      setCurrentOrgId(null);
      setCurrentBotId(null);
      clearSelection();
      setApiWorkspace(null, null);
    }
  }, [credentials]);

  React.useEffect(() => {
    if (!enabled || orgs.length === 0) return;
    const validOrg = currentOrgId && orgs.some((o) => o.orgId === currentOrgId);
    if (!validOrg) {
      const fallback = orgs[0]!.orgId;
      setCurrentOrgId(fallback);
      setCurrentBotId(null);
      writeSelection({ orgId: fallback, botId: null });
    }
  }, [enabled, orgs, currentOrgId]);

  React.useEffect(() => {
    if (!enabled || !currentOrgId || bots.length === 0) return;
    const validBot = currentBotId && bots.some((b) => b.botId === currentBotId);
    if (!validBot) {
      const fallback = bots[0]!.botId;
      setCurrentBotId(fallback);
      writeSelection({ orgId: currentOrgId, botId: fallback });
    }
  }, [enabled, currentOrgId, bots, currentBotId]);

  React.useEffect(() => {
    setApiWorkspace(currentOrgId, currentBotId);
  }, [currentOrgId, currentBotId]);

  const setOrg = React.useCallback(
    (orgId: string) => {
      setCurrentOrgId(orgId);
      setCurrentBotId(null);
      writeSelection({ orgId, botId: null });
      queryClient.invalidateQueries();
    },
    [queryClient],
  );

  const setBot = React.useCallback(
    (botId: string | null) => {
      setCurrentBotId(botId);
      writeSelection({ orgId: currentOrgId ?? '', botId });
      queryClient.invalidateQueries();
    },
    [currentOrgId, queryClient],
  );

  const value = React.useMemo<WorkspaceContextValue>(() => {
    const currentOrg = orgs.find((o) => o.orgId === currentOrgId) ?? null;
    const currentBot = bots.find((b) => b.botId === currentBotId) ?? null;
    return {
      orgs,
      bots,
      currentOrgId,
      currentBotId,
      currentOrg,
      currentBot,
      loading: orgsQuery.isLoading || (!!currentOrgId && botsQuery.isLoading),
      noMemberships: orgsQuery.isSuccess && orgs.length === 0,
      setOrg,
      setBot,
      refreshBots: () => {
        void queryClient.invalidateQueries({ queryKey: ['org', 'bots', currentOrgId] });
      },
    };
  }, [
    orgs,
    bots,
    currentOrgId,
    currentBotId,
    orgsQuery.isLoading,
    orgsQuery.isSuccess,
    botsQuery.isLoading,
    setOrg,
    setBot,
    queryClient,
  ]);

  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace(): WorkspaceContextValue {
  const ctx = React.useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}
